// src/components/Game/GameSetup.tsx
import React, { useState, useEffect } from 'react';
import {
  VStack, Button, Input, NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper, FormControl, FormLabel,
  Text, Box, useBreakpointValue, Flex, Avatar, SimpleGrid,
  // Nouveaux imports pour le RangeSlider
  RangeSlider, RangeSliderTrack, RangeSliderFilledTrack, RangeSliderThumb,
  HStack, useDisclosure, Badge, useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import HelpPopover from '../Tutorial/HelpPopover.tsx';
import { GTMEvents } from '../../utils/gtm.ts';
import CategorySelector from './CategorySelector.tsx';
import { Category } from '../../types/game.ts';

// 1. Mise à jour de l'interface des props
interface GameSetupProps {
  onStartGame: (
    players: { id: number; name: string }[],
    streakToWin: number,
    categoriesToWin: number,
    difficultyRange: [number, number], // On ajoute la plage de difficulté
    selectedCategories: Category[], // Catégories sélectionnées (obligatoire ici)
    mysteryAttackMode: 'classic' | 'roulette', // Nouveau: mode d'attaque mystère en local
    advanced?: {
      classicMalusValue?: number;
      roulettePowers?: Record<string, { enabled: boolean; weight: number }>;
  enableCategoryTimer?: boolean;
  categoryTimerSeconds?: number;
  enableQuestionTimer?: boolean;
  questionTimerSeconds?: number;
    }
  ) => void;
  onBackToLobby?: () => void;
}

const MotionBox = motion(Box);

const GameSetup: React.FC<GameSetupProps> = ({ onStartGame, onBackToLobby }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [numPlayers, setNumPlayers] = useState<number>(2);
  const [playerNames, setPlayerNames] = useState<string[]>([
    `${t('defaultPlayerName')} 1`, 
    `${t('defaultPlayerName')} 2`
  ]);
  const [streakToWin, setStreakToWin] = useState<number>(5);
  const [categoriesToWin, setCategoriesToWin] = useState<number>(3);
  
  // 2. Ajout de l'état pour la plage de difficulté (1 à 5)
  const [difficulty, setDifficulty] = useState<[number, number]>([1, 5]);
  
  // 3. Ajout des catégories sélectionnées
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  // 4. Mode d'attaque mystère (local)
  const [mysteryAttackMode, setMysteryAttackMode] = useState<'classic' | 'roulette'>('roulette');
  // 5. Options avancées
  const [classicMalusValue, setClassicMalusValue] = useState<number>(1);
  const [roulettePowers, setRoulettePowers] = useState<Record<string, { enabled: boolean; weight: number }>>({
    minus1: { enabled: true, weight: 35 },
    minus2: { enabled: true, weight: 25 },
    minus3: { enabled: true, weight: 15 },
    plus1:  { enabled: true, weight: 30 },
    plus2:  { enabled: true, weight: 20 },
    plus3:  { enabled: true, weight: 10 },
    swap1:  { enabled: true, weight: 35 },
    swap2:  { enabled: true, weight: 25 },
  swap3:  { enabled: true, weight: 15 },
  blocked: { enabled: true, weight: 10 },
  nuke: { enabled: true, weight: 5 },
  blind: { enabled: true, weight: 10 },
  megaswap: { enabled: true, weight: 5 },
  translate: { enabled: true, weight: 8 },
  hardcore: { enabled: true, weight: 8 },
  });
  // Timers (local)
  const [enableCategoryTimer, setEnableCategoryTimer] = useState<boolean>(false);
  const [categoryTimerSeconds, setCategoryTimerSeconds] = useState<number>(20);
  const [enableQuestionTimer, setEnableQuestionTimer] = useState<boolean>(false);
  const [questionTimerSeconds, setQuestionTimerSeconds] = useState<number>(20);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [categoriesInitialized, setCategoriesInitialized] = useState(false);
  const [lastCategoryUpdate, setLastCategoryUpdate] = useState(Date.now());
  const { isOpen: isCategorySelectorOpen, onOpen: onCategorySelectorOpen, onClose: onCategorySelectorClose } = useDisclosure();

  const containerWidth = useBreakpointValue({ base: "95%", sm: "85%", md: "500px" });

  // Charger les catégories par défaut au démarrage
  useEffect(() => {
    const loadDefaultCategories = async () => {
      if (!categoriesInitialized) {
        try {
          const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
          const response = await fetch(`${API_URL}/api/categories/${t('lng') || 'fr'}`);
          if (response.ok) {
            const categories = await response.json();
            const defaultCategories = categories.filter((cat: Category) => !cat.is_custom || cat.is_mystery);
            console.log('🎯 [GameSetup] Catégories par défaut chargées:', defaultCategories.map((c: Category) => c.name));
            setSelectedCategories(defaultCategories);
            setCategoriesInitialized(true);
          }
        } catch (error) {
          console.error('❌ [GameSetup] Erreur lors du chargement des catégories:', error);
        }
      }
    };

    loadDefaultCategories();
  }, [categoriesInitialized, t]);

  // Effet pour mettre à jour les noms par défaut quand la langue change
  useEffect(() => {
    setPlayerNames(prev => prev.map((name, index) => {
      // Ne modifier que les noms qui correspondent au format par défaut
      const defaultPattern = /^(Joueur|Player|Jugador|Jogador) \d+$/;
      if (defaultPattern.test(name)) {
        return `${t('defaultPlayerName')} ${index + 1}`;
      }
      return name;
    }));
  }, [t]);

  const handleNumPlayersChange = (_: string, valueAsNumber: number) => {
    const newNum = Math.min(Math.max(2, valueAsNumber || 2), 6);
    setNumPlayers(newNum);
    setPlayerNames(prev => {
      const defaultName = t('playerNamePlaceholder');
      if (newNum > prev.length) {
        return [...prev, ...Array(newNum - prev.length).fill('').map((_, i) => `${defaultName} ${prev.length + i + 1}`)];
      }
      return prev.slice(0, newNum);
    });
  };

  const handleNameChange = (index: number, name: string) => {
    setPlayerNames(prev => {
      const newNames = [...prev];
      newNames[index] = name;
      return newNames;
    });
  };

  const handleSubmit = () => {
    // Vérifier qu'au moins une catégorie est sélectionnée
    if (selectedCategories.length === 0) {
      toast({
        title: 'Catégories requises',
        description: 'Veuillez d\'abord configurer les catégories pour votre partie.',
        status: 'warning',
        duration: 4000,
      });
      return;
    }

    const players = playerNames.map((name, index) => ({
      id: index + 1,
      name: name.trim() || `${t('playerNamePlaceholder')} ${index + 1}`,
    }));
    
    // Tracker la configuration du jeu avant de le démarrer
    GTMEvents.gameStart(
      'local', 
      players.length, 
      `${difficulty[0]}-${difficulty[1]}`
    );
    
    console.log('🎮 [GameSetup] Démarrage avec catégories sélectionnées:', selectedCategories.map(c => c.name));
    
    // Toujours passer les catégories sélectionnées
    onStartGame(
      players,
      streakToWin,
      categoriesToWin,
      difficulty,
      selectedCategories,
      mysteryAttackMode,
      {
        classicMalusValue,
  roulettePowers,
  enableCategoryTimer,
  categoryTimerSeconds,
  enableQuestionTimer,
  questionTimerSeconds,
      }
    );
  };

  const handleCategoriesSelected = (categories: Category[]) => {
    console.log('📦 [GameSetup] Catégories sélectionnées reçues:', categories.map(c => c.name));
    console.log('📦 [GameSetup] Validation du format des catégories:', categories.map(c => ({ id: c.id, name: c.name, is_custom: c.is_custom })));
    
    try {
      // Validation des catégories reçues
      if (!Array.isArray(categories) || categories.some(cat => !cat.id || !cat.name)) {
        console.error('❌ [GameSetup] Format de catégories invalide:', categories);
        toast({
          title: 'Erreur',
          description: 'Format de catégories invalide reçu',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      
      // FORCER la fermeture de la modal au cas où elle serait restée ouverte
      if (isCategorySelectorOpen) {
        console.log('🔧 [GameSetup] Fermeture forcée de la modal depuis GameSetup');
        onCategorySelectorClose();
      }
      
      // Mise à jour des catégories avec gestion d'erreur
      setSelectedCategories(prevCategories => {
        console.log('🔄 [GameSetup] Remplacement de', prevCategories.length, 'catégories par', categories.length);
        return [...categories]; // Créer un nouveau tableau pour forcer le re-render
      });
      
      console.log('✅ [GameSetup] Catégories mise à jour avec succès');
      
    } catch (error) {
      console.error('❌ [GameSetup] Erreur lors de la mise à jour des catégories:', error);
      // En cas d'erreur, forcer la fermeture de la modal
      onCategorySelectorClose();
      toast({
        title: 'Erreur',
        description: 'Problème lors de la mise à jour des catégories',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const containerVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } };

  return (
    <>
      <MotionBox
        initial="hidden" animate="visible" variants={containerVariants}
        w={containerWidth} maxW="500px" p={{ base: 4, md: 8 }} bg="var(--gris-bleute)"
        borderRadius="xl" boxShadow="2xl" border="1px solid" borderColor="rgba(139, 148, 179, 0.2)"
      >
      <VStack spacing={{ base: 6, md: 8 }} w="full">
        <HStack justify="space-between" w="full">
          <h1 className="logo-text" style={{ fontSize: '40px' }}>{t('gameSetupTitle')}</h1>
          <HelpPopover variant="icon" size="md" />
        </HStack>

        <SimpleGrid columns={2} spacing={4} w="full" as={motion.div} variants={itemVariants}>
          <FormControl>
            <FormLabel fontSize="lg" fontWeight="medium" color="var(--texte-clair)">{t('playerCountLabel')}</FormLabel>
            <NumberInput min={2} max={6} value={numPlayers} onChange={handleNumPlayersChange} size="lg" color="var(--texte-clair)">
              <NumberInputField borderRadius="md" bg="var(--nuit-profonde)" borderColor="rgba(139, 148, 179, 0.2)" />
              <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="lg" fontWeight="medium" color="var(--texte-clair)">{t('categoriesToWinLabel')}</FormLabel>
            <NumberInput min={1} max={6} value={categoriesToWin} onChange={(_, val) => setCategoriesToWin(val)} size="lg" color="var(--texte-clair)">
              <NumberInputField borderRadius="md" bg="var(--nuit-profonde)" borderColor="rgba(139, 148, 179, 0.2)" />
              <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
            </NumberInput>
          </FormControl>
        </SimpleGrid>

        <FormControl as={motion.div} variants={itemVariants}>
          <FormLabel fontSize="lg" fontWeight="medium" color="var(--texte-clair)">{t('streakToWinLabel')}</FormLabel>
          <NumberInput min={1} max={10} value={streakToWin} onChange={(_, val) => setStreakToWin(val)} size="lg" color="var(--texte-clair)">
            <NumberInputField borderRadius="md" bg="var(--nuit-profonde)" borderColor="rgba(139, 148, 179, 0.2)" />
            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
          </NumberInput>
        </FormControl>

        {/* 4. Ajout du RangeSlider pour la difficulté */}
<FormControl as={motion.div} variants={itemVariants}>
    <FormLabel 
        id="difficulty-label" // Ajoutez un ID
        fontSize="lg" 
        fontWeight="medium" 
        color="var(--texte-clair)"
    >
        {t('difficultyLabel')} : {(() => {
          const labels = ['Très facile','Facile','Moyen','Difficile','Très difficile'];
          const clamp = (v: number) => Math.max(1, Math.min(5, Math.floor(v)));
          const [a,b] = difficulty || [1,5];
          return (
            <Text as="span" color="var(--bleu-electrique)">
              {`${labels[clamp(a)-1]} → ${labels[clamp(b)-1]}`}
            </Text>
          );
        })()}
    </FormLabel>
  <RangeSlider
    aria-label={["min", "max"]}
        min={1}
        max={5}
        step={1}
        defaultValue={[1, 5]}
    onChangeEnd={(val) => setDifficulty([val[0] as number, val[1] as number])}
    >
        <RangeSliderTrack bg="var(--nuit-profonde)">
            <RangeSliderFilledTrack bg="var(--bleu-electrique)" />
        </RangeSliderTrack>
        <RangeSliderThumb boxSize={6} index={0} />
        <RangeSliderThumb boxSize={6} index={1} />
    </RangeSlider>
</FormControl>

        {/* Sélecteur du mode d'attaque mystère (local) */}
        <FormControl as={motion.div} variants={itemVariants}>
          <FormLabel fontSize="lg" fontWeight="medium" color="var(--texte-clair)">
            Mode d'attaque mystère
          </FormLabel>
          <HStack spacing={4}>
            <Button
              onClick={() => setMysteryAttackMode('classic')}
              variant={mysteryAttackMode === 'classic' ? 'solid' : 'outline'}
              colorScheme="purple"
              size="md"
            >
              Classique (-1)
            </Button>
            <Button
              onClick={() => setMysteryAttackMode('roulette')}
              variant={mysteryAttackMode === 'roulette' ? 'solid' : 'outline'}
              colorScheme="purple"
              size="md"
            >
              Roulette 🎰
            </Button>
          </HStack>
          <Text fontSize="sm" color="var(--texte-secondaire)" mt={2}>
            Choisissez comment se résout la catégorie Mystère en local.
          </Text>
        </FormControl>

        {/* Options avancées — style aligné au lobby multijoueur */}
        <FormControl as={motion.div} variants={itemVariants}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <FormLabel fontSize="lg" fontWeight="medium" color="var(--texte-clair)" m={0}>
              ⚙️ Options avancées
            </FormLabel>
            <button 
              onClick={() => setShowAdvancedOptions(s => !s)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--bleu-electrique)',
                cursor: 'pointer',
                fontSize: '0.9rem',
                padding: '4px 8px',
                borderRadius: '4px'
              }}
              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(79, 140, 255, 0.1)'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
            >
              {showAdvancedOptions ? '▼ Masquer' : '▶ Afficher'}
            </button>
          </div>

          {showAdvancedOptions && (
            <div style={{
              border: '1px solid rgba(139, 180, 243, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              backgroundColor: 'rgba(139, 180, 243, 0.05)',
              marginTop: '8px'
            }}>
              {/* Timers locaux */}
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: 'var(--texte-principal)' }}>
                  ⏱️ Timers (mode local)
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginLeft: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={enableCategoryTimer}
                      onChange={(e) => setEnableCategoryTimer(e.target.checked)}
                    />
                    <span style={{ fontSize: '0.85rem' }}>Timer de choix de catégorie</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '0.85rem', minWidth: '110px' }}>Durée (sec)</span>
                    <input
                      type="number"
                      min={5}
                      max={120}
                      value={categoryTimerSeconds}
                      onChange={(e) => setCategoryTimerSeconds(Math.max(5, Math.min(120, Number(e.target.value) || 20)))}
                      disabled={!enableCategoryTimer}
                      style={{ width: '70px', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(139,180,243,0.3)', backgroundColor: 'rgba(139,180,243,0.1)', color: 'var(--texte-principal)' }}
                    />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={enableQuestionTimer}
                      onChange={(e) => setEnableQuestionTimer(e.target.checked)}
                    />
                    <span style={{ fontSize: '0.85rem' }}>Timer de réponse</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '0.85rem', minWidth: '110px' }}>Durée (sec)</span>
                    <input
                      type="number"
                      min={5}
                      max={120}
                      value={questionTimerSeconds}
                      onChange={(e) => setQuestionTimerSeconds(Math.max(5, Math.min(120, Number(e.target.value) || 20)))}
                      disabled={!enableQuestionTimer}
                      style={{ width: '70px', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(139,180,243,0.3)', backgroundColor: 'rgba(139,180,243,0.1)', color: 'var(--texte-principal)' }}
                    />
                  </div>
                </div>
              </div>
              {/* Configuration mode classique */}
              {mysteryAttackMode === 'classic' && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '0.9rem', 
                    color: 'var(--texte-principal)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    ⚔️ Configuration Mode Classique
                  </h4>
                  <div style={{ marginLeft: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.85rem', minWidth: '120px' }}>
                        💥 Malus appliqué :
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={5}
                        value={classicMalusValue}
                        onChange={(e) => setClassicMalusValue(Math.max(1, Math.min(5, Number(e.target.value) || 1)))}
                        style={{
                          width: '60px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid rgba(139, 180, 243, 0.3)',
                          backgroundColor: 'rgba(139, 180, 243, 0.1)',
                          color: 'var(--texte-principal)',
                          fontSize: '0.85rem'
                        }}
                      />
                      <span style={{ fontSize: '0.8rem', color: 'var(--texte-secondaire)' }}>
                        point{classicMalusValue > 1 ? 's' : ''}
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Configuration mode roulette */}
              {mysteryAttackMode === 'roulette' && (
                <div>
                  <h4 style={{ 
                    margin: '0 0 12px 0', 
                    fontSize: '0.9rem', 
                    color: 'var(--texte-principal)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    🎰 Configuration Roulette des Pouvoirs
                  </h4>

                  {/* Pouvoirs Malus */}
                  <div style={{ marginBottom: '12px' }}>
                    <h5 style={{ 
                      margin: '0 0 6px 0', 
                      fontSize: '0.85rem', 
                      color: '#4F8CFF',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      🔵 Pouvoirs Malus
                    </h5>
                    {['minus1', 'minus2', 'minus3'].map((powerId) => {
                      const power = roulettePowers[powerId];
                      const powerNames: Record<string, string> = {
                        minus1: 'Minus 1 (-1 point)',
                        minus2: 'Minus 2 (-2 points)',
                        minus3: 'Minus 3 (-3 points)'
                      };
                      return (
                        <div key={powerId} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          marginBottom: '4px',
                          marginLeft: '12px'
                        }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px' }}>
                            <input
                              type="checkbox"
                              checked={power.enabled}
                              onChange={() => setRoulettePowers(prev => ({ ...prev, [powerId]: { ...prev[powerId], enabled: !prev[powerId].enabled } }))}
                            />
                            <span style={{ fontSize: '0.8rem' }}>{powerNames[powerId]}</span>
                          </label>
                          {power.enabled && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={power.weight}
                                onChange={(e) => setRoulettePowers(prev => ({ ...prev, [powerId]: { ...prev[powerId], weight: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } }))}
                                style={{
                                  width: '50px',
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                  border: '1px solid rgba(139, 180, 243, 0.3)',
                                  backgroundColor: 'rgba(139, 180, 243, 0.1)',
                                  color: 'var(--texte-principal)',
                                  fontSize: '0.8rem'
                                }}
                              />
                              <span style={{ fontSize: '0.75rem', color: 'var(--texte-secondaire)' }}>%</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Pouvoirs Bonus */}
                  <div style={{ marginBottom: '12px' }}>
                    <h5 style={{ 
                      margin: '0 0 6px 0', 
                      fontSize: '0.85rem', 
                      color: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      🟢 Pouvoirs Bonus
                    </h5>
                    {['plus1', 'plus2', 'plus3'].map((powerId) => {
                      const power = roulettePowers[powerId];
                      const powerNames: Record<string, string> = {
                        plus1: 'Plus 1 (+1 point)',
                        plus2: 'Plus 2 (+2 points)',
                        plus3: 'Plus 3 (+3 points)'
                      };
                      return (
                        <div key={powerId} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          marginBottom: '4px',
                          marginLeft: '12px'
                        }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px' }}>
                            <input
                              type="checkbox"
                              checked={power.enabled}
                              onChange={() => setRoulettePowers(prev => ({ ...prev, [powerId]: { ...prev[powerId], enabled: !prev[powerId].enabled } }))}
                            />
                            <span style={{ fontSize: '0.8rem' }}>{powerNames[powerId]}</span>
                          </label>
                          {power.enabled && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={power.weight}
                                onChange={(e) => setRoulettePowers(prev => ({ ...prev, [powerId]: { ...prev[powerId], weight: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } }))}
                                style={{
                                  width: '50px',
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                  border: '1px solid rgba(139, 180, 243, 0.3)',
                                  backgroundColor: 'rgba(139, 180, 243, 0.1)',
                                  color: 'var(--texte-principal)',
                                  fontSize: '0.8rem'
                                }}
                              />
                              <span style={{ fontSize: '0.75rem', color: 'var(--texte-secondaire)' }}>%</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Pouvoirs Swap */}
                  <div>
                    <h5 style={{ 
                      margin: '0 0 6px 0', 
                      fontSize: '0.85rem', 
                      color: '#FF9800',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      🟠 Pouvoirs Swap
                    </h5>
                    {['swap1', 'swap2', 'swap3'].map((powerId) => {
                      const power = roulettePowers[powerId];
                      const powerNames: Record<string, string> = {
                        swap1: 'Swap 1 (vole 1 point)',
                        swap2: 'Swap 2 (vole 2 points)',
                        swap3: 'Swap 3 (vole 3 points)'
                      };
                      return (
                        <div key={powerId} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          marginBottom: '4px',
                          marginLeft: '12px'
                        }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px' }}>
                            <input
                              type="checkbox"
                              checked={power.enabled}
                              onChange={() => setRoulettePowers(prev => ({ ...prev, [powerId]: { ...prev[powerId], enabled: !prev[powerId].enabled } }))}
                            />
                            <span style={{ fontSize: '0.8rem' }}>{powerNames[powerId]}</span>
                          </label>
                          {power.enabled && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={power.weight}
                                onChange={(e) => setRoulettePowers(prev => ({ ...prev, [powerId]: { ...prev[powerId], weight: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } }))}
                                style={{
                                  width: '50px',
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                  border: '1px solid rgba(139, 180, 243, 0.3)',
                                  backgroundColor: 'rgba(139, 180, 243, 0.1)',
                                  color: 'var(--texte-principal)',
                                  fontSize: '0.8rem'
                                }}
                              />
                              <span style={{ fontSize: '0.75rem', color: 'var(--texte-secondaire)' }}>%</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Pouvoirs Spéciaux */}
                  <div style={{ marginTop: '12px' }}>
                    <h5 style={{ 
                      margin: '0 0 6px 0', 
                      fontSize: '0.85rem', 
                      color: '#E91E63',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      🧿 Pouvoirs Spéciaux
                    </h5>
                    {[
                      { id: 'blocked', label: 'Bloqué (catégorie la plus avancée bloquée 3 tours)' },
                      { id: 'nuke', label: 'Nuke (reset des streaks de tous)' },
                      { id: 'blind', label: 'Aveugle (masque et mélange 3 tours)' },
                      { id: 'megaswap', label: 'Mega Swap (échanges aléatoires pour tous)' },
                      { id: 'translate', label: 'Traduction (langue étrangère 5 tours)' },
                      { id: 'hardcore', label: 'Hardcore (questions niveau max 3 tours)' }
                    ].map(({ id, label }) => {
                      const power = roulettePowers[id];
                      return (
                        <div key={id} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          marginBottom: '4px',
                          marginLeft: '12px'
                        }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '300px' }}>
                            <input
                              type="checkbox"
                              checked={power.enabled}
                              onChange={() => setRoulettePowers(prev => ({ ...prev, [id]: { ...prev[id], enabled: !prev[id].enabled } }))}
                            />
                            <span style={{ fontSize: '0.8rem' }}>{label}</span>
                          </label>
                          {power.enabled && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                value={power.weight}
                                onChange={(e) => setRoulettePowers(prev => ({ ...prev, [id]: { ...prev[id], weight: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } }))}
                                style={{
                                  width: '50px',
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                  border: '1px solid rgba(139, 180, 243, 0.3)',
                                  backgroundColor: 'rgba(139, 180, 243, 0.1)',
                                  color: 'var(--texte-principal)',
                                  fontSize: '0.8rem'
                                }}
                              />
                              <span style={{ fontSize: '0.75rem', color: 'var(--texte-secondaire)' }}>%</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Note explicative */}
                  <div style={{
                    marginTop: '12px',
                    padding: '8px',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    color: 'var(--texte-secondaire)',
                    lineHeight: '1.3'
                  }}>
                    💡 <strong>Note :</strong> Les pourcentages indiquent la probabilité relative de chaque pouvoir. Le total est normalisé automatiquement.
                  </div>
                </div>
              )}
            </div>
          )}
        </FormControl>

        <VStack spacing={4} w="full">
          <Text fontSize="lg" fontWeight="medium" color="var(--texte-clair)">{t('playerNamesLabel')}</Text>
          {playerNames.map((name, index) => (
            <motion.div key={index} variants={itemVariants} style={{ width: '100%' }}>
              <Flex align="center" w="full" gap={3}>
                <Avatar size="sm" name={name || `${t('playerNamePlaceholder')} ${index + 1}`} bg={`hsl(${(index * 60) % 360}, 70%, 60%)`} />
                <Input
                  size="lg" placeholder={`${t('playerNamePlaceholder')} ${index + 1}`} value={name}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  borderRadius="md" bg="var(--nuit-profonde)" borderColor="rgba(139, 148, 179, 0.2)"
                  color="var(--texte-clair)" _focus={{ borderColor: 'var(--bleu-electrique)', boxShadow: '0 0 0 1px var(--bleu-electrique)'}}
                />
              </Flex>
            </motion.div>
          ))}
        </VStack>

        {/* Section de configuration des catégories */}
        <FormControl as={motion.div} variants={itemVariants}>
          <FormLabel fontSize="lg" fontWeight="medium" color="var(--texte-clair)">
            {t('categorySelector.title')}
          </FormLabel>
          <VStack spacing={3} align="stretch">
            <HStack>
              <Button
                onClick={onCategorySelectorOpen}
                variant="outline"
                colorScheme="blue"
                size="lg"
                borderRadius="md"
                bg="var(--nuit-profonde)"
                borderColor="rgba(139, 148, 179, 0.2)"
                color="var(--texte-clair)"
                flex={1}
                _hover={{
                  borderColor: 'var(--bleu-electrique)',
                  bg: 'var(--gris-bleute)'
                }}
              >
                {selectedCategories.length > 0 ? t('categorySelector.configureCategoriesButton') : 'Sélectionner les catégories (Obligatoire)'}
                {selectedCategories.length > 0 && (
                  <Badge ml={2} colorScheme="blue">
                    {selectedCategories.length}
                  </Badge>
                )}
              </Button>
              
              {/* Bouton d'urgence pour fermer la modal si elle reste bloquée */}
              {isCategorySelectorOpen && (
                <Button
                  onClick={() => {
                    console.log('🚨 [GameSetup] Fermeture d\'urgence de la modal');
                    onCategorySelectorClose();
                  }}
                  variant="solid"
                  colorScheme="red"
                  size="lg"
                  title="Fermer la modal si elle est bloquée"
                >
                  ✕
                </Button>
              )}
            </HStack>
            
            {selectedCategories.length > 0 && (
              <Box
                p={3}
                bg="var(--gris-bleute)"
                borderRadius="md"
                border="1px solid"
                borderColor="rgba(139, 148, 179, 0.2)"
              >
                <Text fontSize="sm" color="var(--texte-secondaire)" mb={2}>
                  Catégories sélectionnées : {selectedCategories.length}
                </Text>
                <Flex wrap="wrap" gap={2}>
                  {selectedCategories.slice(0, 6).map((category) => (
                    <Badge
                      key={category.id}
                      colorScheme={category.is_custom ? "purple" : "blue"}
                      variant="solid"
                      fontSize="xs"
                      style={category.color ? {
                        borderLeft: `4px solid ${category.color}`,
                        paddingLeft: '8px'
                      } : undefined}
                    >
                      {category.is_custom && (category.emoji || '👤') + ' '}
                      {category.name}
                    </Badge>
                  ))}
                  {selectedCategories.length > 6 && (
                    <Badge colorScheme="gray" variant="solid" fontSize="xs">
                      +{selectedCategories.length - 6} autres
                    </Badge>
                  )}
                </Flex>
              </Box>
            )}
          </VStack>
        </FormControl>

        <VStack spacing={4} w="full">
          <Button
            as={motion.button} variants={itemVariants} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            size="lg" onClick={handleSubmit} w="full" h="60px" fontSize="lg" className="quizvs-button"
            isDisabled={selectedCategories.length === 0}
            opacity={selectedCategories.length === 0 ? 0.6 : 1}
          >
            {selectedCategories.length === 0 ? 'Configurer les catégories d\'abord' : t('startGameButton')}
          </Button>
          
          <Button
            size="lg"
            onClick={onBackToLobby || (() => window.location.reload())}
            variant="outline"
            colorScheme="blue"
            w="full"
            h="50px"
          >
            {t('backToLobby')}
          </Button>
        </VStack>
      </VStack>
    </MotionBox>
    
    {/* Modal de sélection des catégories */}
    {isCategorySelectorOpen && (
      <CategorySelector
        isOpen={isCategorySelectorOpen}
        onClose={onCategorySelectorClose}
        onCategoriesSelected={handleCategoriesSelected}
        initialSelectedCategories={selectedCategories}
      />
    )}
    </>
  );
};

export default GameSetup;